import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, PanelLeft, PlusCircle, Search, X } from "lucide-react";
import { iconList } from "../../utils/iconList";
import { SidebarIcon } from "./sidebarIcons";
import useAuth from "../../hooks/useAuth";
import { roleRoutes } from "../../utils/roles";
import { getMenuSections } from "../../config/menuConfig";
import CreatePackageWizard from "../createPackageWizard";
import { ModernConfirmation } from "../confirmation";
import Modal from "../modal";
import { useSelectedItem } from "./SelectedItemContext";
import { useSidebar } from "../../providers/SidebarProvider";
import useSidebarBranding from "../../hooks/useSidebarBranding";
import useTenantModules from "../../hooks/useTenantModules";
import { getPendingAddressesCount } from "../../api/packages";
import { isCommerceRole } from "../productNews/commerceMenu";
import { useNewsSummary } from "../productNews/newsSummaryStore";

// Identidad estable para "sin módulos" mientras la consulta no está lista.
const NO_TENANT_MODULES = [];

// Chips que usan el color de marca en vez del rojo base. El rojo queda para "MU"
// (maqueta en saneamiento) y para los contadores, que avisan de trabajo
// pendiente: un "Beta" en rojo se leería como un error en una herramienta que la
// empresa recién recibe.
const BRAND_BADGES = new Set(["NUEVO", "Beta"]);

// ─────────────────────────────────────────────────────────────────────────────
// Item de navegación individual con tooltip integrado
// La posición del tooltip se calcula síncronamente en mouseEnter para evitar
// flash en posición 0,0 en el primer render.
// ─────────────────────────────────────────────────────────────────────────────
const NavItem = ({ item, active, isCollapsed, onNavigate }) => {
  const { t } = useTranslation();
  const btnRef = useRef(null);
  const [tooltipState, setTooltipState] = useState({ visible: false, top: 0, left: 0 });
  const label = t(item.label);
  // La marca se pinta traducida ("Bêta", "测试版"); las comparaciones usan la
  // clave tal cual, que es lo que declara menuConfig.
  const badge = item.badge ? t(item.badge) : null;
  const brandBadge = BRAND_BADGES.has(item.badge);

  const handleMouseEnter = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setTooltipState({
        visible: true,
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipState((prev) => ({ ...prev, visible: false }));
  };

  return (
    <>
      <button
        ref={btnRef}
        className={`csb-nav-item${active ? " active" : ""}`}
        onClick={() => onNavigate(item.path)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={badge ? `${label} (${badge})` : label}
        aria-current={active ? "page" : undefined}
        type="button"
      >
        <SidebarIcon name={item.icon} className="csb-nav-item__icon" />
        <span className="csb-nav-item__label">{label}</span>
        {badge && (
          <>
            {/* Chip en expandido; en el rail se convierte en un punto de color
                (mismo patrón que el rail del mockup). MU = maqueta en
                saneamiento (rojo) · NUEVO y Beta = feature nueva (brand). */}
            <span
              className={`csb-nav-item__badge${brandBadge ? " csb-nav-item__badge--nuevo" : ""}`}
              title={item.badge === "MU" ? t("Vista portada del mockup, en saneamiento") : undefined}
            >
              {badge}
            </span>
            <span
              className={`csb-nav-item__dot${brandBadge ? " csb-nav-item__dot--nuevo" : ""}`}
              aria-hidden="true"
            />
          </>
        )}
      </button>

      {/* Tooltip fixed-position (escapa el overflow del sidebar) */}
      {isCollapsed && tooltipState.visible && (
        <div
          className="sidebar-tooltip"
          style={{ top: tooltipState.top, left: tooltipState.left }}
          aria-hidden="true"
        >
          {label}
          {badge && <span className="sidebar-tooltip__badge">{badge}</span>}
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

// Buscador del menú oculto hasta nuevo aviso (pedido 2026-08-01): el código
// queda entero detrás de este flag — para reactivarlo, ponerlo en true.
const MOSTRAR_BUSCADOR = false;

const CollapsibleSidebar = ({ fetch, forceExpanded = false }) => {
  const { t } = useTranslation();
  const { user, userData } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selectedItem } = useSelectedItem();
  const { isCollapsed: railCollapsed, toggleCollapsed } = useSidebar();
  // El drawer móvil reusa este componente SIEMPRE expandido: el estado rail
  // persistido de desktop no debe filtrarse al panel (renderizaría solo íconos).
  const isCollapsed = forceExpanded ? false : railCollapsed;

  const [openModal, setOpenModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [confirmationDocument, setConfirmationDocument] = useState(false);
  const [packagesCreated, setPackagesCreated] = useState([]);

  // Co-branding: cuando el operador lo activa, el sidebar expandido muestra SOLO
  // su logo, en vez del de Beartrack. Aplica igual a los usuarios del operador y
  // a los de los comercios que trabajan con él; el backend resuelve cuál es el
  // operador a partir del JWT. El logo de Beartrack sobrevive únicamente en el
  // rail colapsado.
  //
  // `coBranding` ya viene garantizado con logo: el backend nunca lo devuelve en
  // true sin un logo cargado, para que la cabecera no quede vacía.
  const { coBranding, logo: companyLogo, operatorName } = useSidebarBranding();
  const companyLabel = operatorName || t("Empresa");
  const [logoFallido, setLogoFallido] = useState(false);
  // Si la imagen del operador no carga (URL rota, B2 caído), se vuelve al logo
  // de Beartrack. Sin esto la cabecera quedaría vacía justo en el caso en que el
  // operador más quiere verse.
  useEffect(() => { setLogoFallido(false); }, [companyLogo]);
  const mostrarSoloLogoOperador = coBranding && !!companyLogo && !logoFallido;

  // ── Buscador del menú ──────────────────────────────────────
  const [busqueda, setBusqueda] = useState("");
  const buscadorRef = useRef(null);
  const [focoPendiente, setFocoPendiente] = useState(false);

  // La lupa del rail expande el sidebar y deja el foco en el input.
  useEffect(() => {
    if (!isCollapsed && focoPendiente) {
      buscadorRef.current?.focus();
      setFocoPendiente(false);
    }
  }, [isCollapsed, focoPendiente]);

  // Sin acentos ni mayúsculas: "analitica" encuentra "Analítica".
  const normalizar = (s) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const filtro = normalizar(busqueda.trim());

  const handleNavigate = (path) => {
    setBusqueda("");
    navigate(path);
  };

  // ¿La ruta actual cae bajo el path de este ítem? Es solo la coincidencia
  // individual: cuál ítem queda activo lo decide `isActive` más abajo.
  const matchesRoute = (path) => {
    if (path === "/admin") {
      return pathname === "/admin" || pathname === "/admin/";
    }
    if (path === "/home") {
      return pathname === "/home" || pathname === "/home/";
    }
    // /plataforma es el Overview del superadmin: match exacto, o quedaría
    // activo en todas las subvistas de /plataforma/*.
    if (path === "/plataforma") {
      return pathname === "/plataforma" || pathname === "/plataforma/";
    }
    // /holding es el dashboard del holding admin: match exacto. Sin esto,
    // startsWith("/holding/") matchea /holding/companies, /holding/packages,
    // etc. y deja Dashboard activo en TODAS las pantallas del sidebar.
    if (path === "/holding") {
      return pathname === "/holding" || pathname === "/holding/";
    }
    if (path === "/admin/adminFleet" && pathname === "/admin/adminFleet/map") {
      return false;
    }
    if (pathname === path) return true;
    if (pathname.startsWith(path + "/") || pathname.startsWith(path + "?")) {
      return true;
    }
    return false;
  };

  const isClient =
    user?.role === roleRoutes.client;

  // Los ítems condicionados a un módulo de la empresa (el Rutificador) aparecen
  // recién con la respuesta en mano: mientras carga, o si falló, no se ofrecen.
  const { status: tenantModulesStatus, modules, tenantType } = useTenantModules();
  const tenantModules = tenantModulesStatus === "ready" ? modules : NO_TENANT_MODULES;

  const sectionsBase = getMenuSections(user?.role, selectedItem, {
    hasOwnFleet: user?.company?.hasOwnFleet,
    tenantModules,
    tenantType,
  });

  // Contadores dinámicos del menú. Hoy sólo el de direcciones por resolver, que
  // no es informativo: mientras el número sea > 0 hay pedidos que no pueden
  // entrar a una ruta, y quien arma el reparto necesita verlo sin abrir la
  // pantalla.
  //
  // El menú declara `badgeKey`; acá se resuelve contra el backend y se traduce
  // al `badge` que el sidebar ya sabe pintar, en vez de agregar un mecanismo
  // paralelo. Un fallo se traga a propósito: el contador es un apoyo, no puede
  // romper la navegación.
  const [badgeCounts, setBadgeCounts] = useState({});

  useEffect(() => {
    let cancelled = false;
    const necesita = sectionsBase.some((s) =>
      s.items?.some((i) => i.badgeKey === "pendingAddresses"),
    );
    if (!necesita) return undefined;

    getPendingAddressesCount()
      .then(({ data }) => {
        if (!cancelled) {
          setBadgeCounts((prev) => ({ ...prev, pendingAddresses: data?.count ?? 0 }));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // Se recalcula al navegar: así el número baja apenas alguien resuelve una
    // dirección y vuelve al listado, sin necesidad de recargar la página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Etiqueta "NUEVO" en los ítems que señala una novedad que este usuario
  // todavía no visitó (se apaga al entrar: ver NewsBell). Mismo `badge` que el
  // resto; si el ítem ya tiene uno (un contador, "Beta") gana ese.
  const newsSummary = useNewsSummary(isCommerceRole(user?.role) ? user?.id ?? null : null);
  const newPaths = useMemo(
    () => new Set(newsSummary.menuHighlights.map((h) => h.path)),
    [newsSummary.menuHighlights],
  );

  const sections = useMemo(
    () =>
      sectionsBase.map((section) => ({
        ...section,
        items: section.items?.map((item) => {
          let next = item;
          if (item.badgeKey) {
            const n = badgeCounts[item.badgeKey];
            if (n > 0) next = { ...item, badge: String(n) };
          }
          return !next.badge && newPaths.has(next.path) ? { ...next, badge: "NUEVO" } : next;
        }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sectionsBase), badgeCounts, newPaths],
  );

  // Entre todos los ítems que coinciden con la ruta gana el path MÁS LARGO,
  // el mismo criterio con el que el header (newAdminMenu) elige su título.
  // Sin esto, en /admin/helpdesk/incidencias quedaban activos a la vez "Mesa
  // de ayuda" (por prefijo) y "Atención e incidencias", ambos con
  // aria-current="page". Se recorren todas las secciones, no las filtradas por
  // el buscador: qué pantalla está abierta no depende de lo que se busque.
  let activePath = null;
  sections.forEach((section) => {
    section.items?.forEach((item) => {
      if (matchesRoute(item.path) && (activePath === null || item.path.length > activePath.length)) {
        activePath = item.path;
      }
    });
  });
  const isActive = (path) => path === activePath;

  // Con búsqueda activa solo quedan las secciones con match (por label de
  // ítem, o la sección completa si el match es su título).
  const seccionesVisibles = !filtro
    ? sections
    : sections
        .map((section) => {
          if (normalizar(t(section.title)).includes(filtro)) return section;
          const items = section.items.filter((it) => normalizar(t(it.label)).includes(filtro));
          return items.length ? { ...section, items } : null;
        })
        .filter(Boolean);

  // ── Secciones colapsables ──────────────────────────────────────
  const SECTIONS_STORAGE_KEY = "beartrack.sidebar.openSections";

  // Sección que contiene la ruta activa (se mantiene abierta).
  const activeSectionTitle = useMemo(() => {
    const match = sections.find((s) => s.items.some((it) => isActive(it.path)));
    return match?.title || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, pathname]);

  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY));
      if (Array.isArray(saved)) return new Set(saved);
    } catch {
      // ignorar parseo inválido
    }
    return new Set();
  });

  // Al navegar, abrir siempre la sección de la ruta actual.
  useEffect(() => {
    if (!activeSectionTitle) return;
    setOpenSections((prev) => {
      if (prev.has(activeSectionTitle)) return prev;
      const next = new Set(prev);
      next.add(activeSectionTitle);
      return next;
    });
  }, [activeSectionTitle]);

  // Persistir abierto/cerrado entre sesiones.
  useEffect(() => {
    try {
      localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify([...openSections]));
    } catch {
      // ignorar (cuota/privado)
    }
  }, [openSections]);

  const toggleSection = (title) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  // Saludo unificado para todos los roles: nombre + apellido de la persona.
  // Fallback al nombre de la empresa si el perfil aún no trae nombre.
  const displayName =
    [userData?.name || user?.name, userData?.lastName || user?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || userData?.company?.name || "";

  return (
    <>
      <aside
        className={`collapsible-sidebar${isCollapsed ? " sidebar--collapsed" : " sidebar--expanded"}`}
        aria-label={t("Menú de navegación")}
      >
        {/* ── Header: logo + contraer/expandir ─────────────────────── */}
        <div className="csb-header">
          {isCollapsed ? (
            /* Rail: el isotipo (wordmark recortado a su 32% izquierdo, el oso)
               ES el botón de expandir — mismo patrón que el mockup. */
            <button
              className="csb-mark-btn"
              onClick={toggleCollapsed}
              aria-label={t("Expandir menú")}
              aria-expanded={false}
              title={t("Expandir menú")}
              type="button"
            >
              <span className="csb-mark" aria-hidden="true">
                <img src={iconList.logoBeartrack} alt="" className="csb-mark__img" />
              </span>
            </button>
          ) : (
            <>
              <div className="csb-header__row">
                <div className={`csb-header__logo-wrap${mostrarSoloLogoOperador ? " csb-header__logo-wrap--cobrand" : ""}`}>
                  {mostrarSoloLogoOperador ? (
                    <img
                      src={companyLogo}
                      alt={companyLabel}
                      title={companyLabel}
                      className="csb-header__co-logo as-company-logo"
                      onError={() => setLogoFallido(true)}
                    />
                  ) : (
                    <img
                      src={iconList.logoBeartrack}
                      alt="Beartrack"
                      className="csb-header__logo"
                    />
                  )}
                </div>
                {/* En el drawer móvil no hay rail: el botón de contraer sobra.
                    Ícono PanelLeft pelado (sin flecha), como claude.ai/ChatGPT. */}
                {!forceExpanded && (
                  <button
                    className="csb-collapse-btn"
                    onClick={() => {
                      setBusqueda("");
                      toggleCollapsed();
                    }}
                    aria-label={t("Colapsar menú")}
                    aria-expanded
                    title={t("Colapsar menú")}
                    type="button"
                  >
                    <PanelLeft size={16} strokeWidth={2} aria-hidden="true" />
                  </button>
                )}
              </div>
              <p className="csb-header__greeting">{t("Hola, {{name}}", { name: displayName })}</p>
            </>
          )}
        </div>

        {/* ── Buscador del menú (oculto: MOSTRAR_BUSCADOR) ─────────── */}
        {!MOSTRAR_BUSCADOR ? null : isCollapsed ? (
          <div className="csb-search-rail">
            <button
              className="csb-search-rail__btn"
              onClick={() => {
                setFocoPendiente(true);
                toggleCollapsed();
              }}
              aria-label={t("Buscar en el menú")}
              title={t("Buscar en el menú")}
              type="button"
            >
              <Search size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className="csb-search">
            <Search size={14} strokeWidth={2} className="csb-search__icon" aria-hidden="true" />
            <input
              ref={buscadorRef}
              className="csb-search__input"
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => {
                // Esc limpia la búsqueda (y no cierra el drawer si hay texto).
                if (e.key === "Escape" && busqueda) {
                  e.stopPropagation();
                  setBusqueda("");
                }
              }}
              placeholder={t("Buscar en el menú")}
              aria-label={t("Buscar en el menú")}
            />
            {busqueda && (
              <button
                className="csb-search__clear"
                onClick={() => {
                  setBusqueda("");
                  buscadorRef.current?.focus();
                }}
                aria-label={t("Limpiar búsqueda")}
                type="button"
              >
                <X size={12} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {/* ── Navegación ───────────────────────────────────────────── */}
        <nav className="csb-nav" aria-label={t("Navegación principal")}>
          {!isCollapsed && filtro && seccionesVisibles.length === 0 && (
            <div className="csb-search-empty">
              {t("Sin resultados para")} “{busqueda.trim()}”
            </div>
          )}
          {seccionesVisibles.map((section, sIdx) => {
            if (isCollapsed) {
              // Rail: sin encabezados de sección — hairlines entre grupos e
              // ítems solo-ícono con tooltip (patrón del rail del mockup).
              return (
                <div className="csb-rail-group" key={`${section.title}-${sIdx}`}>
                  {sIdx > 0 && <div className="csb-rail-divider" aria-hidden="true" />}
                  {section.items.map((item, iIdx) => (
                    <NavItem
                      key={`${item.path}-${iIdx}`}
                      item={item}
                      active={isActive(item.path)}
                      isCollapsed
                      onNavigate={handleNavigate}
                    />
                  ))}
                </div>
              );
            }
            // Buscando, todas las secciones con match quedan abiertas.
            const open = !!filtro || openSections.has(section.title);
            // Plegada con un ítem "NUEVO" adentro: un punto en la cabecera para
            // que no quede oculto. No se despliega sola: respeta lo que dejó el
            // usuario.
            const hiddenNew = !open && section.items.some((item) => item.badge === "NUEVO");
            return (
              <div
                className={`csb-section${open ? " csb-section--open" : ""}`}
                key={`${section.title}-${sIdx}`}
              >
                {/* Encabezado de sección — botón que pliega/despliega */}
                <button
                  type="button"
                  className="csb-section__header"
                  onClick={() => toggleSection(section.title)}
                  aria-expanded={open}
                >
                  <span className="csb-section__title">
                    {t(section.title)}
                    {hiddenNew && (
                      <>
                        <span
                          className="ml-1.5 inline-block h-2 w-2 rounded-full bg-brand-500 align-middle"
                          aria-hidden="true"
                          data-testid="csb-section-new-dot"
                        />
                        <span className="sr-only">{t("(tiene novedades)")}</span>
                      </>
                    )}
                  </span>
                  <ChevronDown size={14} strokeWidth={2} className="csb-section__chevron" aria-hidden="true" />
                </button>

                {/* Items (solo si la sección está abierta) */}
                {open &&
                  section.items.map((item, iIdx) => (
                    <NavItem
                      key={`${item.path}-${iIdx}`}
                      item={item}
                      active={isActive(item.path)}
                      isCollapsed={false}
                      onNavigate={handleNavigate}
                    />
                  ))}
              </div>
            );
          })}
        </nav>

        {/* ── Footer: "Crear pedido" (solo clientes) ───────────────── */}
        {/* El menú de usuario se movió al header (ver UserMenu en mainLayout). */}
        {isClient && (
          <div className="csb-footer">
            <button
              className="csb-create-btn"
              onClick={() => setOpenModal(true)}
              type="button"
              aria-label={t("Crear pedido")}
              title={isCollapsed ? t("Crear pedido") : undefined}
            >
              <PlusCircle size={16} strokeWidth={2} className="csb-create-btn__icon" aria-hidden="true" />
              <span className="csb-create-btn__label">{t("Crear pedido")}</span>
            </button>
          </div>
        )}
      </aside>

      {/* ── Modales (solo clientes) ──────────────────────────────────── */}
      {isClient && (
        <>
          <Modal
            open={openModal}
            setOpen={() => setOpenModal(false)}
            size="xl"
            bodyClassName="wizard-modal-body"
            body={
              <CreatePackageWizard
                closeModal={() => setOpenModal(false)}
                fetch={fetch}
                setConfirmationModal={setConfirmationModal}
                setPackagesCreated={setPackagesCreated}
              />
            }
          />

          <Modal
            open={confirmationModal}
            setOpen={() => setConfirmationModal(false)}
            body={
              <ModernConfirmation
                mssg={t("La encomienda se creó correctamente.")}
                packages={packagesCreated}
                setConfirmationModal={setConfirmationModal}
                fetch={fetch}
              />
            }
            centered
          />

          <Modal
            open={confirmationDocument}
            setOpen={() => setConfirmationDocument(false)}
            body={
              <ModernConfirmation
                mssg={t("El archivo se ha subido correctamente.")}
                packages={packagesCreated}
                setConfirmationModal={setConfirmationDocument}
              />
            }
            centered
          />
        </>
      )}
    </>
  );
};

export default CollapsibleSidebar;
